import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "governance", title: "Governance" },
  { id: "security", title: "Security" },
  { id: "privacy", title: "Privacy & Data Handling" },
  { id: "availability", title: "Availability" },
  { id: "subprocessors", title: "Subprocessors" },
  { id: "accessibility", title: "Accessibility" },
  { id: "compliance", title: "Compliance & Certifications" },
  { id: "contact", title: "Contact" },
];

const Trust = () => (
  <>
    <Helmet>
      <title>Trust Center — The Emerging Market</title>
      <meta
        name="description"
        content="How The Emerging Market protects data, maintains uptime, respects privacy, and stays accessible to Alabama communities."
      />
      <link rel="canonical" href="https://emrkt.co/trust" />
      <meta property="og:title" content="Trust Center — The Emerging Market" />
      <meta property="og:url" content="https://emrkt.co/trust" />
      <meta property="og:type" content="website" />
    </Helmet>
    <LegalLayout
      title="Trust Center"
      description="One page to find how we handle security, privacy, uptime, subprocessors, and accessibility. Maintained by The Emerging Market to answer common questions from partners, funders, and agencies."
      lastUpdated="July 2, 2026"
      sections={sections}
    >
      <section id="overview">
        <h2>1. Overview</h2>
        <p>
          The Emerging Market ("TEM") is a civic-data platform for Alabama
          communities and the agencies, funders, and advocates working
          alongside them. This Trust Center is maintained by TEM to answer
          the questions we hear most often about security, privacy, uptime,
          and accessibility.
        </p>
        <p>
          Nothing on this page is an independent certification. Where we
          describe practices provided by our hosting or infrastructure
          partners, we say so — and we distinguish those from commitments we
          make ourselves as the app owner.
        </p>
      </section>

      <section id="governance">
        <h2>2. Governance</h2>
        <p>
          The Emerging Market is an Alabama public benefit corporation
          organized as a limited liability company (LLC). We are not a
          501(c)(3) nonprofit and do not accept tax-deductible donations. Our
          work is funded through services, subscriptions, and contracts.
        </p>
        <p>
          As a public benefit corporation, we operate with the stated purpose
          of building effective and scalable strategies for grassroots
          organizations and businesses in Alabama.
        </p>
      </section>

      <section id="security">
        <h2>3. Security</h2>
        <p>
          We enforce authentication, row-level authorization on every table,
          role-based admin access, encrypted secrets, TLS in transit,
          encryption at rest, daily backups, and error monitoring. Members can
          enable multi-factor authentication and generate recovery codes from{" "}
          <a href="/account">Account Settings</a>.
        </p>
        <ul>
          <li>Full detail: <a href="/security">Security commitments</a></li>
          <li>Incident handling: <a href="/incident-response">Incident Response</a></li>
          <li>Report a vulnerability: <a href="mailto:info@emrkt.co">info@emrkt.co</a> (subject "Security report")</li>
        </ul>
      </section>

      <section id="privacy">
        <h2>4. Privacy &amp; Data Handling</h2>
        <p>
          We collect the minimum data needed to operate the platform and let
          you opt in — never opt out — to aggregated research use of your
          activity. Retention windows, deletion rights, and enterprise data
          processing terms are published.
        </p>
        <ul>
          <li><a href="/privacy">Privacy Policy</a></li>
          <li><a href="/dpa">Data Processing Agreement</a></li>
          <li><a href="/retention">Records Retention &amp; Deletion Policy</a></li>
          <li><a href="/cookies">Cookie Policy</a></li>
          <li><a href="/acceptable-use">Acceptable Use Policy</a></li>
        </ul>
      </section>

      <section id="availability">
        <h2>5. Availability</h2>
        <p>
          Uptime, incident timeline, and current component status are
          published in near real time. We do not promise a contractual SLA on
          the free tiers; institutional customers receive written uptime
          terms in their agreement.
        </p>
        <ul>
          <li>Live status: <a href="/status">emrkt.co/status</a></li>
          <li>Backups: daily database backups with quarterly restore drills</li>
        </ul>
      </section>

      <section id="subprocessors">
        <h2>6. Subprocessors</h2>
        <p>
          We use a small set of vetted infrastructure and communication
          providers to run the platform. Current subprocessors and their role
          are listed in the DPA and updated when we add or remove one.
        </p>
        <ul>
          <li><a href="/dpa">Data Processing Agreement — Subprocessors</a></li>
        </ul>
      </section>

      <section id="accessibility">
        <h2>7. Accessibility</h2>
        <p>
          We build against WCAG 2.1 AA as our working target and prioritize
          keyboard navigation, screen-reader labels, and color-contrast tokens
          throughout the app. See our full{" "}
          <a href="/accessibility">Accessibility Statement</a> for what's
          implemented today, what's in progress, and how to report a barrier.
        </p>
      </section>

      <section id="compliance">
        <h2>8. Compliance &amp; Certifications</h2>
        <p>
          TEM is not currently SOC 2, ISO 27001, HIPAA, or PCI certified. We
          describe the controls we run honestly and do not claim regulatory
          compliance we have not earned. Certifications on our credentials
          roadmap will appear here when they are in place.
        </p>
        <p>
          Institutional customers who need contractual security terms should
          request our <a href="/dpa">Data Processing Agreement</a>.
        </p>
      </section>

      <section id="contact">
        <h2>9. Contact</h2>
        <p>
          Security, privacy, and trust questions:{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a>. We aim to
          acknowledge within 3 business days.
        </p>
        <p className="text-xs italic">
          This page is app-owned editable content maintained by The Emerging
          Market. It is not an independent audit or certification, and does
          not create contractual obligations outside of a signed agreement.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default Trust;